import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  keywords: string[];
  path?: string;
  /** Open Graph type: "article" for blog posts, "website" for everything else. */
  type?: "website" | "article";
}

const SITE_NAME = "HBT Integrisys";
const SITE_URL = "https://www.hbtintegrisys.com";

export function buildFullTitle(title: string) {
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
}

// Captured synchronously during renderToString() by the prerender script
// (scripts/prerender.mjs), since useEffect never runs during SSR. Reset
// before each render so a stale value can't leak into the next route.
export let lastRenderedSeo: {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type: string;
} | null = null;

function setMetaByName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-route SEO manager. Sets document title, meta description, keywords, canonical
 * URL, and Open Graph / Twitter tags on route change for client-side
 * navigations. The same title/description are also baked into each
 * route's static HTML at build time by scripts/prerender.mjs, since this
 * component's useEffect never runs for a crawler that doesn't execute JS.
 */
export function Seo({ title, description, keywords, path = "/", type = "website" }: SeoProps) {
  const keywordsContent = keywords.join(", ");

  if (typeof window === "undefined") {
    lastRenderedSeo = { title, description, keywords: keywordsContent, path, type };
  }

  useEffect(() => {
    const fullTitle = buildFullTitle(title);
    document.title = fullTitle;

    setMetaByName("description", description);
    setMetaByName("keywords", keywordsContent);
    setCanonical(`${SITE_URL}${path}`);

    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", `${SITE_URL}${path}`);
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:site_name", SITE_NAME);

    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", description);
  }, [title, description, keywordsContent, path, type]);

  return null;
}
